import { useCallback } from 'react'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'

import { NOOP } from 'Utils/stableDefaults'

// Proposed on [Contract] Scheduled change create / cancel (CU-124g39pcvv2): the legacy plan_switch
// route, taking `target` instead of `to` with subscription_plans_restructure on.
export const SCHEDULED_CHANGE_PATH = '/profile/subscription/plan_switch'

// Contract v3: the POST refuses a change while an upgrade or a renewal is being applied.
const UPGRADE_IN_PROGRESS = 'upgrade_in_progress'
const RENEWAL_IN_PROGRESS = 'renewal_in_progress'
const EXPECTED_SCHEDULE_CODES = [UPGRADE_IN_PROGRESS, RENEWAL_IN_PROGRESS]

const errorCode = (error) => error?.response?.data?.code
const isExpectedScheduleError = (error) => EXPECTED_SCHEDULE_CODES.includes(errorCode(error))

function useScheduledChange({ onChange = NOOP, messages }) {
  const { postData, isLoading: isScheduling } = useApiPost()
  const { deleteData, isLoading: isKeepingPlan } = useApiDelete()
  const { showError } = useNotifications()

  const send = useCallback((request, data, errorMessageFor, isExpectedError) => new Promise((resolve) => {
    request({
      path: SCHEDULED_CHANGE_PATH,
      data,
      isExpectedError,
      onSuccess: (response) => {
        onChange(response.data?.scheduledChange ?? null)
        resolve(true)
      },
      onError: (error) => {
        showError(errorMessageFor(error))
        resolve(false)
      },
    })
  }), [onChange, showError])

  const { scheduleError, upgradeInProgressError, renewalInProgressError, keepError } = messages

  const scheduleErrorFor = useCallback((error) => {
    const code = errorCode(error)
    if (code === UPGRADE_IN_PROGRESS) return upgradeInProgressError
    if (code === RENEWAL_IN_PROGRESS) return renewalInProgressError
    return scheduleError
  }, [scheduleError, upgradeInProgressError, renewalInProgressError])

  const scheduleChange = useCallback(
    (target) => send(postData, { target }, scheduleErrorFor, isExpectedScheduleError),
    [send, postData, scheduleErrorFor]
  )

  const keepPlan = useCallback(
    () => send(deleteData, undefined, () => keepError),
    [send, deleteData, keepError]
  )

  return {
    scheduleChange, isScheduling, keepPlan, isKeepingPlan,
  }
}

export default useScheduledChange
